import React, { useCallback, useEffect, useState } from 'react';
import ReusableButton from '../common/Button/Button';
import { IBalloonDetails } from '../../type/balloonDetails';
import operationAPI from '../../api/operationApi';
import { IPilotDetails } from '../../type/pilot';
import { useForm, SubmitHandler } from 'react-hook-form';
import { IAddPilot } from '../../type/addPilot';
import CrossIcon from '../../assets/CrossIcon';
import { IStaffAllocation } from '../../type/staffAllocation';
import { IBusDetails } from '../../type/busDetails';
import useLocationStore from '../../store/locatonStore';
import CheckIcon from '../../assets/CheckIcon';
import { StaffConstant } from '../../constants/constant';
import { toast } from 'react-toastify';
import { translate } from '../../i18n';
import { encodeDateForUrl } from '../../utils/dateUtils';
import BallIcon from '../../assets/BallIcon';
import RedCrossIcon from '../../assets/icon/RedCrossIcon';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';

interface PilotFormProps {
  heading: string;
  closeModal: () => void;
  balloons: IBalloonDetails[];
  crewVehicleList: IBusDetails[];
  operationStaffId?: number;
  initialValues?: IStaffAllocation;
  onStaffChange: () => void;
  staffAllocation?: IStaffAllocation[] | null;
  date: Date;
}

const PilotForm: React.FC<PilotFormProps> = ({
  heading,
  closeModal,
  balloons,
  operationStaffId,
  initialValues,
  onStaffChange,
  staffAllocation,
  date,
}) => {
  const [selectedStaffId, setSelectedStaffId] = useState<number>();
  const { selectedLocation } = useLocationStore();
  const [isLoading, setIsLoading] = useState(false);
  const [actualWeight, setActualWeight] = useState(false);
  const [maxPos, setMaxPos] = useState<number>();
  const [addAnother, setAddAnother] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch
  } = useForm<IAddPilot>();
  
  const [pilotList, setPilotList] = useState<IPilotDetails[]>();
  const [selectedBalloon, setSelectedBalloon] = useState<IBalloonDetails | null>(null);

  const watchedBalloonCapacity = watch('balloonCapacity');

  useEffect(() => {
    if (initialValues) {
      const initialBalloon = balloons.find(
        (balloon) => balloon.balloonId == initialValues.balloonId,
      );
      
      setSelectedBalloon(initialBalloon || null);
      setValue('staffId', initialValues?.staffId);
      setValue('balloonId', initialValues?.balloonId);
      
      const initialCapacity = initialValues?.maxPax || initialValues?.balloonMaxPax;
      setValue('balloonCapacity', initialCapacity);
      
      if (!selectedStaffId) {
        setSelectedStaffId(initialValues?.staffId);
      }
    }
  }, [initialValues, balloons, setValue]);

  const getMaxPos = (staffAllocation: IStaffAllocation[]): number => {
    if (staffAllocation.length === 0) return 0;
    return Math.max(...staffAllocation.map((staff) => staff.pos));
  };

  useEffect(() => {
    fetchStaffList();
    const pos = getMaxPos(staffAllocation ?? []);
    setMaxPos(pos + 1);
  }, []);

  const fetchStaffList = useCallback(async () => {
    try {
      const response = await operationAPI.pilotList(
        selectedLocation.OwnerId,
        date,
      );

      const excludedStaffIds =
        staffAllocation?.map((staff) => staff.staffId) || [];
      const filteredDriverList =
        response?.data?.filter(
          (driver: IPilotDetails) => !excludedStaffIds.includes(driver.staffId),
        ) || [];
      if (initialValues?.staffId) {
        const staffFromInitialValues = response?.data?.find(
          (driver: IPilotDetails) => driver.staffId === initialValues.staffId,
        );
        if (staffFromInitialValues) {
          filteredDriverList.push(staffFromInitialValues);
        }
      }
      setPilotList(filteredDriverList);
    } catch (error) {
      console.log(error);
    }
  }, [initialValues, pilotList, staffAllocation]);

  const handleBalloonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const balloonId = parseInt(event.target.value);
    const balloon = balloons.find((b) => b.balloonId === balloonId) || null;
    setSelectedBalloon(balloon);
    
    if (balloon) {
      setValue('balloonCapacity', balloon?.maxPax);
    } else {
      setValue('balloonCapacity', undefined);
    }
  };

  function sanitizeData(data: any): any {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) =>
        value === '' ? [key, null] : [key, value],
      ),
    );
  }

  const resetForm = () => {
    reset();
    setSelectedStaffId(undefined);
    setSelectedBalloon(null);
    setValue('balloonCapacity', undefined);
    setValue('phoneNumber', '');
  };

  const onSubmit: SubmitHandler<IAddPilot> = async (data) => {
    setIsLoading(true);
    if (operationStaffId || initialValues?.balloonId) {
      try {
        await operationAPI.updatePilot({
          ...data,
          operationStaffId: operationStaffId,
          ownerId: selectedLocation.OwnerId,
          roleId: StaffConstant.PILOT,
          date: encodeDateForUrl(date),
          isUpdatePassenger: true,
          operationBalloonFlightId: initialValues?.operationBallonFlightId ? initialValues?.operationBallonFlightId : undefined
        });
        setIsLoading(false);
        toast.success(translate('pilot.pilotUpdated'));
        if (addAnother) {
          resetForm();
          setIsLoading(false);
          onStaffChange();
        } else {
          setTimeout(() => {
            closeModal();
            onStaffChange();
          }, 500);
        }
      } catch (error) {
        setIsLoading(false);
        console.error('Failed to update pilot:', error);
        toast.warn(translate('operation.commonError'));
        closeModal();
      }
    } else {
      try {
        const sanitizedData = sanitizeData({
          ...data,
          ownerId: selectedLocation.OwnerId,
          roleId: StaffConstant.PILOT,
          date: encodeDateForUrl(date),
          pos: maxPos,
        });
        const response = await operationAPI.addPilot({
          ...sanitizedData,
        });
        toast.success(translate('pilot.pilotAdded'));
        if (addAnother) {
          resetForm();
          setIsLoading(false);
          onStaffChange();
        } else {
          setTimeout(() => {
            closeModal();
            onStaffChange();
          }, 500);
        }
      } catch (error) {
        setIsLoading(false);
        console.error('Failed to add driver:', error);
        toast.warn(translate('operation.commonError'));
        closeModal();
      }
    }
  };

  const selectedBalloonIds = staffAllocation
    ? staffAllocation
        .filter((operation) => operation.balloonId !== initialValues?.balloonId)
        .map((operation) => operation.balloonId)
    : [];

  const filteredBalloons = balloons.filter(
    (balloon) =>
      !selectedBalloonIds.includes(balloon.balloonId) ||
      balloon.balloonId === initialValues?.balloonId,
  );

  return (
    <>
      <div className="max-w-md mx-auto  bg-sidebar-background p-6 rounded-lg shadow-md relative">
        {/* Title and Close Button */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-[var(--textColor)]">
            {heading}
          </h2>
          <button
            className="text-textColorGray hover:text-textColorGray"
            onClick={closeModal}
          >
            {/* Close Icon */}
            <RedCrossIcon />
          </button>
        </div>
        <div className="border-b-2 border-[#EEF7FF] mb-4"></div>
        {/* Form */}

        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className="overflow-y-auto max-h-[calc(100vh-100px)] min-w-full grid grid-cols-2 gap-4">
            {/* Pilot Name */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('pilot.pilotName')}
              </label>
              <select
                {...register('staffId', {
                  onChange: (e) => {
                    const value = e.target.value;
                    setSelectedStaffId(value);
                    setValue('staffId', value);
                    const selectedDriver = pilotList?.find(
                      (driver) => driver?.staffId === Number(value),
                    );
                    if (selectedDriver) {
                      setValue('phoneNumber', selectedDriver?.staffContact);
                    }
                  },
                })}
                value={selectedStaffId}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
              >
                {pilotList && pilotList.length > 0 ? (
                  <>
                    <option value="">{translate('pilot.selectPilot')}</option>
                    {pilotList?.map((pilot: IPilotDetails) => (
                      <option value={pilot?.staffId} key={pilot?.staffId}>
                        {pilot?.staffName}
                        {pilot?.staffqualifications
                          ? ` (${pilot.staffqualifications})`
                          : ''}
                      </option>
                    ))}
                  </>
                ) : null}
              </select>
              {errors?.staffId && (
                <span className="text-red-500">
                  {translate('pilot.pilotValidation')}
                </span>
              )}
            </div>

            {/* Balloon */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.balloon')}
              </label>
              <select
                {...register('balloonId')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                onChange={handleBalloonChange}
                value={selectedBalloon?.balloonId || ''}
                defaultValue={initialValues?.operationBalloonId}
              >
                <option value="">{translate('driver.selectBalloon')}</option>
                {filteredBalloons.map((balloon) => (
                  <option value={balloon?.balloonId} key={balloon?.balloonId}>
                    {balloon?.formattedBalloonName}
                  </option>
                ))}
              </select>
              {errors.balloonId && (
                <span className="text-red-500">
                  {translate('driver.balloonValidation')}
                </span>
              )}
            </div>
            
            {/* Phone Number */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.phoneNumber')}
              </label>
              <input
                type="text"
                {...register('phoneNumber')}
                placeholder="Phone Number"
                readOnly
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                defaultValue={initialValues?.resPhone.replace(
                  /^[^\d]*(\d.*)/,
                  '$1',
                )}
              />
            </div>
            
            {/* Balloon Capacity */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('pilot.balloonCapacity')}
              </label>
            <input
                type="text"
                {...register('balloonCapacity')}
                placeholder="Balloon Capacity"
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md bg-backgroundColor-darkThemeColor text-text-secondary"
                value={watchedBalloonCapacity ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setValue('balloonCapacity', value === '' ? undefined : parseInt(value));
                }}
            />
            </div>

            {/* Checkboxes */}
            <div className="col-span-2 flex flex-col gap-2">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  {...register('need')}
                  className="w-5 h-5 mr-2 text-[var(--textColor)]"
                  defaultChecked={initialValues?.need}
                />
                <span className="text-sm text-textColorGray">
                  {translate('operationCommon.need')}
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  {...register('kkd')}
                  className="w-5 h-5 mr-2 text-[var(--textColor)]"
                  defaultChecked={initialValues?.kkd}
                />
                <span className="text-sm text-textColorGray">
                  {translate('operationCommon.kkd')}
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  {...register('actualWeight')}
                  defaultChecked={initialValues?.actualWeight}
                  onChange={(e) => {
                    setActualWeight(e.target.checked);
                  }}
                  className="w-5 h-5 mr-2 text-[var(--textColor)]"
                />
                <span className="text-sm text-textColorGray">
                  {translate('pilot.actualWeight')}
                </span>
                {(initialValues?.actualWeight || actualWeight) && <BallIcon />}
              </label>
            </div>
          </div>
          
          {/* Buttons */}
          <div className="col-span-2 flex mt-4">
            <div className="flex w-auto items-center space-x-2">
              <input
                type="checkbox"
                id="addAnother"
                className="w-5 h-5 border-light-blue"
                checked={addAnother}
                onChange={(e) => setAddAnother(e.target.checked)}
              />
              <label
                htmlFor="addAnother"
                className="text-sm whitespace-nowrap mb-1 text-textColorGray"
              >
                {translate('common.addAnother')}
              </label>
            </div>
            <div className="flex w-full justify-end">
              <ReusableButton
                variant="secondary"
                children={'Cancel'}
                icon={
                  <div>
                    <div className="block dark:hidden">
                      <CrossIcon />
                    </div>
                    <div className="hidden dark:block">
                      <CrossIconDarkForm />
                    </div>
                  </div>
                }
                onClick={closeModal}
                isDisabled={isLoading}
                className="w-auto bg-button-cancelButtonBgColor text-text-cancelButtonTextColor"
              />
              <ReusableButton
                icon={<CheckIcon />}
                children={'Confirm'}
                variant="primary"
                className="w-auto bg-background-backgroundColor"
                isDisabled={isLoading}
                isLoader={isLoading}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default PilotForm;